import React, { useState } from 'react';

/**
 * YouTubeLite - Lightweight YouTube embed with facade pattern
 *
 * Loads only a thumbnail (~50KB) initially instead of full YouTube player (~1.1MB).
 * The actual YouTube iframe loads only when user clicks play.
 *
 * Performance benefits:
 * - Saves ~1.1MB on initial page load
 * - Reduces thread blocking time by ~765ms
 * - Improves Core Web Vitals (LCP, TBT)
 *
 * @param {string} videoId - YouTube video ID (e.g., "pbuqxY9vJZU")
 * @param {string} title - Video title for accessibility
 */
const YouTubeLite = ({ videoId, title }) => {
  const [activated, setActivated] = useState(false);

  const handleClick = () => {
    setActivated(true);
  };

  return (
    <div className="aspect-w-16 aspect-h-9 rounded-xl overflow-hidden shadow-2xl bg-dark-gray relative">
      {!activated ? (
        // Lightweight facade: thumbnail + play button
        <div
          className="absolute inset-0 cursor-pointer group"
          onClick={handleClick}
          role="button"
          tabIndex={0}
          aria-label={`Play ${title}`}
          onKeyPress={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleClick();
            }
          }}
        >
          {/* YouTube Thumbnail - maxresdefault for best quality */}
          <img
            src={`https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`}
            alt={title}
            className="w-full h-full object-cover"
            loading="lazy"
          />

          {/* Dark overlay on hover for better play button visibility */}
          <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>

          {/* YouTube Play Button - SVG replica of official button */}
          <div className="absolute inset-0 flex items-center justify-center">
            <svg
              className="w-16 h-16 sm:w-20 sm:h-20 text-red-600 drop-shadow-lg transition-transform duration-200 group-hover:scale-110"
              viewBox="0 0 68 48"
              aria-hidden="true"
            >
              {/* Red YouTube button background */}
              <path
                fill="currentColor"
                fillOpacity="0.9"
                d="M66.5 7.7c-.8-2.9-2.5-5.4-5.4-6.2C55.8.1 34 0 34 0S12.2.1 6.9 1.6c-3 .8-4.6 3.3-5.4 6.2C.1 13.2 0 24 0 24s.1 10.8 1.5 16.3c.8 2.9 2.5 5.4 5.4 6.2C12.2 47.9 34 48 34 48s21.8-.1 27.1-1.6c3-.8 4.6-3.3 5.4-6.2C67.9 34.8 68 24 68 24s-.1-10.8-1.5-16.3z"
              />
              {/* White play triangle */}
              <path
                fill="#fff"
                d="M45 24L27 14v20"
              />
            </svg>
          </div>

          {/* Accessibility: Screen reader hint */}
          <span className="sr-only">Click to load YouTube video</span>
        </div>
      ) : (
        // Full YouTube iframe - loads only after user interaction
        <iframe
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
          title={title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full absolute inset-0"
        />
      )}
    </div>
  );
};

export default YouTubeLite;
